import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  User,
  Search,
  Mail,
  CheckCircle,
  Clock,
  Loader2,
  ShieldCheck,
  Plus,
  Users,
  Ban,
  MoreHorizontal,
  Eye,
  Archive,
  ArchiveRestore,
  ChevronUp,
  ChevronDown,
  ChevronDownIcon,
  X,
  SlidersHorizontal,
  RefreshCw,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetTitle, SheetClose } from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import SmartPagination from '@/components/ui/smart-pagination';
import { PaginationParams } from '@/lib/types';
import { PractitionerStaff } from '@/services/practitionerStaffService';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { permissionCheck } from '@/lib/permission-check';
import { useClinic } from '@/contexts/ClinicContext';
import { Progress } from '@/components/ui/progress';
import { practitionerStaffService } from '@/services/practitionerStaffService';
import ArchivePractitionerStaffDialog from '@/components/dialog/ArchivePractitionerStaffDialog';
import AddPractitionerStaff from '@/components/drawer/AddPractitionerStaff';
import { Skeleton } from '@/components/ui/skeleton';
import StatusBadgeWithTooltipStaff from '@/components/StatusBadgeWithTooltipStaff';
import { format } from 'date-fns';

const PractitionerStaffManagement = () => {
  const { user } = useAuth();
  const { isPractitioner, isClinicStaff, isAdmin, rolePermissions, clinic } = useClinic();
  const [searchTerm, setSearchTerm] = useState('');
  const [practitionerStaffs, setPractitionerStaffs] = useState<PractitionerStaff[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showAddPractitionerStaffDrawer, setShowAddPractitionerStaffDrawer] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    status: '',
    itemsPerPage: 10,
    totalItems: 0,
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  // Tabs and Filter Drawer states
  const [activeTab, setActiveTab] = useState('staff');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [tempStatusFilter, setTempStatusFilter] = useState('');

  // Dialog states
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<PractitionerStaff | null>(null);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ChevronDown className='h-3 w-3 text-slate-300' />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp className='h-3 w-3' />
    ) : (
      <ChevronDown className='h-3 w-3' />
    );
  };

  const handlePageChange = (page: number) => {
    setPagination({
      ...pagination,
      page: page,
    });
  };

  const fetchPractitionerStaff = async (currentStatusFilter?: string) => {
    try {
      setLoading(true);
      const response = await practitionerStaffService.getClinicPractitionerStaff(
        clinic?.id!,
        {
          page: pagination.page,
          limit: pagination.limit,
          search: pagination.search,
          sortBy: sortField,
          sortOrder: sortOrder.toUpperCase() as 'ASC' | 'DESC',
          status: currentStatusFilter ?? statusFilter,
        }
      );
      setPractitionerStaffs(response.data.practitionerStaff);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (error) {
      console.error('Error fetching practitioner staffs:', error);
      toast({
        title: 'Error fetching practitioner staffs',
        description: 'Please try again later',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const debouncedSearch = setTimeout(() => {
      setPagination(prev => ({
        ...prev,
        page: 1,
        search: searchTerm,
      }));
    }, 500);
    return () => clearTimeout(debouncedSearch);
  }, [searchTerm]);

  useEffect(() => {
    fetchPractitionerStaff(statusFilter);
  }, [pagination.page, pagination.limit, pagination.search, sortField, sortOrder, statusFilter]);


  const getStatusFilterLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'pending_acceptance':
        return 'Pending Acceptance';
      case 'pending':
        return 'Provisioning';
      case 'archived':
        return 'Deactivated';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const handleArchive = (staff: PractitionerStaff) => {
    setSelectedStaff(staff);
    setArchiveDialogOpen(true);
  };

  // Rotate colors for staff icons
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-600' },
    { bg: 'bg-purple-100', text: 'text-purple-600' },
    { bg: 'bg-pink-100', text: 'text-pink-600' },
    { bg: 'bg-orange-100', text: 'text-orange-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { bg: 'bg-rose-100', text: 'text-rose-600' },
    { bg: 'bg-violet-100', text: 'text-violet-600' },
    { bg: 'bg-teal-100', text: 'text-teal-600' },
  ];

  const hasCreatePermission = permissionCheck(
    'practitioner_staff:create',
    rolePermissions,
    isPractitioner || isClinicStaff || isAdmin
  );

  return (
    <div className='space-y-4 sm:space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>
            Practitioner Staff
          </h1>
          <p className='mt-1 text-sm text-slate-500'>
            Manage your clinic's practitioner staff members
          </p>
        </div>
        {hasCreatePermission && (
          <div className='flex gap-2'>
            <Button
              onClick={() => fetchPractitionerStaff()}
              variant='outline'
              className='rounded-lg border-brand-teal font-medium text-brand-teal shadow-sm hover:bg-brand-teal/10'
            >
              <RefreshCw className='h-4 w-4' />
              Refresh
            </Button>
            <Button
              onClick={() => setShowAddPractitionerStaffDrawer(true)}
              className='rounded-lg bg-brand-teal font-medium text-white shadow-sm hover:bg-brand-teal/90'
            >
              <Plus className='h-4 w-4' />
              Add Staff
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
        <TabsList
          className='h-auto w-full justify-start gap-1 rounded-lg border border-slate-200 p-1 sm:w-auto'
          style={{ backgroundColor: 'rgb(245, 247, 247)' }}
        >
          <TabsTrigger
            value='staff'
            className='flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm'
          >
            <Users className='h-4 w-4' />
            Staff Members
          </TabsTrigger>
        </TabsList>

        <TabsContent value='staff' className='mt-4 space-y-4'>
          {/* Search and Filter Row */}
          <div className='flex flex-row items-center gap-3'>
            {/* Search */}
            <div className='relative max-w-sm flex-1'>
              <Search className='absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400' />
              <Input
                placeholder='Search by name, email...'
                className='rounded-lg border-slate-200 bg-white pl-9'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value.trimStart())}
              />
            </div>

            {/* Filter Button */}
            <Button
              variant='outline'
              size='sm'
              className='ml-auto h-9 gap-2 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-700'
              onClick={() => {
                setTempStatusFilter(statusFilter);
                setIsFilterDrawerOpen(true);
              }}
            >
              <SlidersHorizontal className='h-4 w-4' />
              Filter
              {statusFilter && (
                <Badge className='ml-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-0 bg-red-500 px-1.5 py-0.5 text-xs text-white'>
                  1
                </Badge>
              )}
            </Button>
          </div>

      {/* Active Filters Pills */}
      {statusFilter && (
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-xs text-slate-500'>Filters:</span>
          <Badge
            variant='secondary'
            className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
          >
            Status: {getStatusFilterLabel(statusFilter)}
            <button
              onClick={() => {
                setStatusFilter('');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className='ml-1.5 hover:text-slate-900'
            >
              <X className='h-3 w-3' />
            </button>
          </Badge>
          <button
            onClick={() => {
              setStatusFilter('');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className='text-xs text-brand-teal hover:underline'
          >
            Clear all
          </button>
        </div>
      )}

          {/* Table */}
          <div className='overflow-hidden rounded-lg border border-slate-200 bg-white'>
        <div className='overflow-x-auto'>
          <table className='w-full min-w-[700px] text-sm'>
            <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
              <tr className='border-b border-slate-200'>
                <th
                  className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  #
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('full_name')}
                >
                  <div className='flex items-center gap-1'>
                    Name
                    <SortIcon field='full_name' />
                  </div>
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Email
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Phone
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Clinic
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Status
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('created_at')}
                >
                  <div className='flex items-center gap-1'>
                    Created At
                    <SortIcon field='created_at' />
                  </div>
                </th>
                <th
                  className='sticky right-0 w-12 px-2 py-3'
                  style={{
                    background:
                      'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className='border-b border-slate-100'>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-10' />
                    </td>
                    <td className='px-4 py-3'>
                      <div className='flex items-center gap-3'>
                        <Skeleton className='h-8 w-8 rounded-lg' />
                        <Skeleton className='h-4 w-28' />
                      </div>
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-36' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-24' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-20' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-5 w-16 rounded-full' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-8 w-8 rounded' />
                    </td>
                  </tr>
                ))
              ) : practitionerStaffs.length === 0 ? (
                <tr>
                  <td colSpan={7} className='h-48 text-center'>
                    <div className='flex flex-col items-center justify-center text-slate-500'>
                      <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                        <Users className='h-7 w-7 text-slate-400' />
                      </div>
                      <p className='font-medium text-slate-600'>
                        {searchTerm ? 'No staff members found' : 'No staff members yet'}
                      </p>
                      <p className='mt-1 text-sm text-slate-400'>
                        {searchTerm
                          ? 'Try adjusting your search'
                          : 'Add your first staff member to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                practitionerStaffs.map((staff, index) => {
                  const color = colors[index % colors.length];
                  return (
                    <tr
                      key={staff.id}
                      className='border-b border-slate-100 transition-colors hover:bg-slate-50/50'
                    >
                      <td
                        className='whitespace-nowrap px-4 py-3 text-slate-500'
                        style={{ fontSize: '0.8rem' }}
                      >
                        #
                        {String((pagination.page - 1) * pagination.limit + index + 1).padStart(
                          3,
                          '0'
                        )}
                      </td>
                      <td
                        className='max-w-[200px] whitespace-nowrap px-4 py-3'
                        style={{ fontSize: '0.8rem' }}
                      >
                        <div className='flex items-center gap-3'>
                          <div
                            className={`h-8 w-8 ${color.bg} flex flex-shrink-0 items-center justify-center rounded-lg`}
                          >
                            <User className={`h-4 w-4 ${color.text}`} />
                          </div>
                          <div className='min-w-0'>
                            <span className='block max-w-[150px] truncate font-medium capitalize text-slate-900'>
                              {staff.full_name}
                            </span>
                            <div className='mt-0.5 flex flex-wrap gap-1'>
                              {staff.is_clinic_admin && (
                                <Badge className='border-0 bg-brand-copper px-1.5 py-0 text-[10px] text-white hover:bg-brand-copper whitespace-nowrap'>
                                  <ShieldCheck className='mr-1 h-2.5 w-2.5' />
                                  Clinic Admin
                                </Badge>
                              )}
                              <Badge className='border-0 bg-slate-100 px-1.5 py-0 text-[10px] text-slate-700 hover:bg-slate-100 whitespace-nowrap'>
                                Practitioner Staff
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        className='max-w-[180px] px-4 py-3 text-slate-600'
                        style={{ fontSize: '0.8rem' }}
                      >
                        <span className='block truncate' title={staff.email || '--'}>
                          {staff.email || '--'}
                        </span>
                      </td>
                      <td
                        className='whitespace-nowrap px-4 py-3 text-slate-600'
                        style={{ fontSize: '0.8rem' }}
                      >
                        {staff.phone_number || '--'}
                      </td>
                      <td
                        className='whitespace-nowrap px-4 py-3 text-slate-600'
                        style={{ fontSize: '0.8rem' }}
                      >
                        {staff.clinic?.name || '--'}
                      </td>

                      <td className='whitespace-nowrap px-4 py-3'>
                        <div className='flex flex-col gap-1'>
                          <StatusBadgeWithTooltipStaff
                            status={staff.status}
                            userStatus={staff.user?.status}
                          />
                          {staff.status === 'pending' && (
                            <Progress value={staff.rpa_progress} className='h-1.5 w-20' />
                          )}
                        </div>
                      </td>
                      <td
                        className='whitespace-nowrap px-4 py-3 text-slate-600'
                        style={{ fontSize: '0.8rem' }}
                      >
                        {staff.created_at ? format(new Date(staff.created_at), 'MMM dd, yyyy') : '--'}
                      </td>
                      <td
                        className='sticky right-0 w-12 px-2 py-3'
                        style={{
                          background:
                            'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                        }}
                      >
                        {(staff.status === 'active' || staff.status === 'archived') &&
                          permissionCheck(
                            'practitioner_staff:update',
                            rolePermissions,
                            isPractitioner || isClinicStaff || isAdmin
                          ) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8 hover:bg-slate-100'
                                >
                                  <MoreHorizontal className='h-4 w-4 text-slate-500' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end' className='w-40'>
                                {staff.status === 'active' && (
                                  <DropdownMenuItem
                                    onClick={() => handleArchive(staff)}
                                    className='cursor-pointer text-red-600 text-xs'
                                    disabled={user?.id === staff.user_id}
                                  >
                                    <Archive className='mr-2 h-4 w-4' />
                                    Deactivate
                                  </DropdownMenuItem>
                                )}
                                {staff.status === 'archived' && (
                                  <DropdownMenuItem
                                    onClick={() => handleArchive(staff)}
                                    className='cursor-pointer text-emerald-600 text-xs'
                                    disabled={user?.id === staff.user_id}
                                  >
                                    <ArchiveRestore className='mr-2 h-4 w-4' />
                                    Activate
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className='flex flex-col items-center gap-3 border-t border-slate-200 px-2 py-3 sm:flex-row sm:justify-between sm:px-4'>
            <SmartPagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              className='mx-0 justify-center sm:justify-start'
              maxVisiblePages={3}
            />
            <p className='whitespace-nowrap text-xs text-slate-500'>
              Total: <span className='font-medium text-slate-700'>{pagination.totalItems}</span>{' '}
              Staff Members
            </p>
          </div>
        )}
      </div>
        </TabsContent>
      </Tabs>

      {/* Filter Drawer */}
      <Sheet open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
        <SheetContent className='w-full sm:max-w-md'>
          <SheetTitle className='text-lg font-semibold text-slate-900'>
            Filter Staff
          </SheetTitle>
          <SheetClose className='absolute right-4 top-4' />
          
          <div className='mt-6 space-y-4'>
            {/* Status Filter */}
            <div className='space-y-2'>
              <Label className='text-sm font-medium text-slate-700'>Status</Label>
              <Select value={tempStatusFilter} onValueChange={setTempStatusFilter}>
                <SelectTrigger className='h-10 rounded-lg border-slate-200'>
                  <SelectValue placeholder='All Statuses' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=''>All Statuses</SelectItem>
                  <SelectItem value='active'>Active</SelectItem>
                  <SelectItem value='pending_acceptance'>Pending Acceptance</SelectItem>
                  <SelectItem value='pending'>Provisioning</SelectItem>
                  <SelectItem value='archived'>Deactivated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className='absolute bottom-0 left-0 right-0 flex gap-2 border-t border-slate-200 bg-white p-4'>
            <Button
              variant='outline'
              className='flex-1'
              onClick={() => {
                setTempStatusFilter('');
              }}
            >
              Clear All
            </Button>
            <Button
              className='flex-1 bg-brand-teal hover:bg-brand-teal/90'
              onClick={() => {
                setStatusFilter(tempStatusFilter);
                setPagination(prev => ({ ...prev, page: 1 }));
                setIsFilterDrawerOpen(false);
              }}
            >
              Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Dialogs */}
      <AddPractitionerStaff
        open={showAddPractitionerStaffDrawer}
        onOpenChange={setShowAddPractitionerStaffDrawer}
        onSuccess={() => {
          fetchPractitionerStaff();
        }}
        clinicId={clinic?.id!}
      />
      {archiveDialogOpen && selectedStaff && (
        <ArchivePractitionerStaffDialog
          open={archiveDialogOpen}
          onOpenChange={setArchiveDialogOpen}
          practitionerStaff={selectedStaff}
          onSuccess={() => {
            setArchiveDialogOpen(false);
            setSelectedStaff(null);
            fetchPractitionerStaff();
          }}
        />
      )}
    </div>
  );
};

export default PractitionerStaffManagement;
